// savedJobsApi.js — the /saved-jobs fetchers (rules and bodies: savedJobs.js).
//
// Every route needs Authorization, so all calls go through authFetch, as the
// Library's do. Errors carry the backend's detail.code (job_not_saveable,
// job_already_saved, saved_job_not_found — contract 1.15) for the UI to route on.

import { authFetch } from './auth';
import { API_BASE } from './config';
import { SAVED_JOB_ROUTES, cartonPaths } from './savedJobs';

export class SavedJobsApiError extends Error {
  constructor(message, status, code, body) {
    super(message);
    this.name = 'SavedJobsApiError';
    this.status = status;
    this.code = code || null;
    this.body = body || null;
  }
}

async function call(apiBase, [route, opts], signal) {
  const init = { method: opts.method || 'GET', signal };
  if (opts.body) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(opts.body);
  }
  const resp = await authFetch(`${apiBase}${route}`, init);
  if (!resp.ok) {
    let body = null;
    try { body = await resp.json(); } catch { /* non-JSON */ }
    const code = body?.detail?.code || null;
    const msg = body?.detail?.message
      || (Array.isArray(body?.detail) && body.detail[0]?.msg)
      || code || `Request failed (${resp.status})`;
    throw new SavedJobsApiError(msg, resp.status, code, body);
  }
  return resp.status === 204 ? null : resp.json();
}

export async function listSavedJobs({ apiBase = API_BASE, signal } = {}) {
  const body = await call(apiBase, SAVED_JOB_ROUTES.list(), signal);
  return Array.isArray(body?.items) ? body.items : [];
}

export function getSavedJob({ apiBase = API_BASE, id, signal }) {
  return call(apiBase, SAVED_JOB_ROUTES.get(id), signal);
}

export function saveJob({ apiBase = API_BASE, jobId, name, cartonIds, setup, signal }) {
  return call(apiBase, SAVED_JOB_ROUTES.save({ jobId, name, cartonIds, setup }), signal);
}

export function renameSavedJob({ apiBase = API_BASE, id, name, signal }) {
  return call(apiBase, SAVED_JOB_ROUTES.rename(id, name), signal);
}

export function removeSavedJob({ apiBase = API_BASE, id, signal }) {
  return call(apiBase, SAVED_JOB_ROUTES.remove(id), signal);
}

/**
 * A reopened job's carton and its file (#88), from where `via` says:
 * { savedJobId } — the saved job's own routes, decided 2 Nov (b); or
 * { includeDeleted: true } — the Library, deleted cartons included (the last
 * run). Same answers as library.js's getCarton / getCartonSource.
 */
export async function fetchCarton({ apiBase = API_BASE, id, via, signal }) {
  return call(apiBase, [cartonPaths(id, via).carton, {}], signal);
}

export async function fetchCartonSource({ apiBase = API_BASE, id, filename, via, signal }) {
  const resp = await authFetch(`${apiBase}${cartonPaths(id, via).source}`, { signal });
  if (!resp.ok) {
    let body = null;
    try { body = await resp.json(); } catch { /* non-JSON */ }
    throw new SavedJobsApiError(body?.detail?.message || `Request failed (${resp.status})`,
      resp.status, body?.detail?.code || null, body);
  }
  const blob = await resp.blob();
  const safe = filename && /\.pdf$/i.test(filename) ? filename : `${filename || id}.pdf`;
  return new File([blob], safe, { type: 'application/pdf' });
}
